import React, { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { SearchBar } from '@/components/inventory/SearchBar';
import { BarcodeScanner } from '@/components/inventory/BarcodeScanner';
import { useSales } from '@/hooks/useSales';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Edit, AlertTriangle, Package, Trash2, QrCode, Minus, Plus, Camera, DollarSign, BarChart3, TrendingUp, Copy, RefreshCw } from 'lucide-react';
import { QRCodeModal } from '@/components/ui/qr-code-modal';
import { Checkbox } from '@/components/ui/checkbox';
import { Product } from '@/context/SupabaseInventoryContext';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell } from 'recharts';
import { toast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DuplicateProductsModal } from './DuplicateProductsModal';

// Hook pour détecter la taille d'écran
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState<'small' | 'medium' | 'large'>('medium');
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize('small');
      } else if (width < 1024) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  return screenSize;
};
interface SupabaseProductTableProps {
  onEditProduct?: (p: Product) => void;
  initialSelectedProducts?: Set<string>;
  onSelectionChange?: (selectedProducts: Set<string>) => void;
}
export interface SupabaseProductTableRef {
  activateLowStockFilter: () => void;
}
export const SupabaseProductTable = forwardRef<SupabaseProductTableRef, SupabaseProductTableProps>(({
  onEditProduct,
  initialSelectedProducts,
  onSelectionChange
}, ref) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const {
    products,
    visibleProducts,
    hiddenProducts,
    deleteProduct,
    updateProduct
  } = useSupabaseInventory();
  const {
    sales
  } = useSales();
  const {
    userRole
  } = useCompany();
  const {
    hasAccess
  } = useSubscription();
  const screenSize = useScreenSize();
  const isAdmin = userRole === 'admin';

  // sélection, recherche, filtre, pagination
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(initialSelectedProducts || new Set());
  const [isUpdatingFromOpenFood, setIsUpdatingFromOpenFood] = useState(false);
  const autoEnrichedRef = useRef(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicates, setDuplicates] = useState<Array<{key: string; products: Product[]; type: 'barcode' | 'name'}>>([]);

  // Synchroniser les sélections initiales quand elles changent
  useEffect(() => {
    if (initialSelectedProducts) {
      setSelectedProducts(initialSelectedProducts);
    }
  }, [initialSelectedProducts]);

  // Synchroniser les changements de sélection avec le parent
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedProducts);
    }
  }, [selectedProducts, onSelectionChange]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Stock limit adaptatif selon la taille d'écran
  const getResponsiveStockLimit = (size: 'small' | 'medium' | 'large') => {
    switch (size) {
      case 'small':
        return 10;
      case 'medium':
        return 20;
      case 'large':
        return 50;
      default:
        return 20;
    }
  };
  const [stockLimit, setStockLimit] = useState<number>(getResponsiveStockLimit(screenSize));

  // Mettre à jour stockLimit quand la taille d'écran change
  useEffect(() => {
    setStockLimit(getResponsiveStockLimit(screenSize));
  }, [screenSize]);

  // quantités locales — on stocke string | number to allow empty during typing
  const [localQuantities, setLocalQuantities] = useState<Record<string, string | number>>({});
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Formatage prix
  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);

  // --- Filtrage (search + low stock) ---
  const filteredVisibleProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return visibleProducts.filter(p => {
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q);
    }).filter(p => showLowStockOnly ? p.quantity <= p.min_threshold : true);
  }, [visibleProducts, searchQuery, showLowStockOnly]);
  const filteredHiddenProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return hiddenProducts.filter(p => {
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q);
    }).filter(p => showLowStockOnly ? p.quantity <= p.min_threshold : true);
  }, [hiddenProducts, searchQuery, showLowStockOnly]);

  // Combine visible and hidden for display
  const filteredProducts = [...filteredVisibleProducts, ...filteredHiddenProducts];

  // Reset pagination when search/filter change (fix pagination conflict)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showLowStockOnly, rowsPerPage]);

  // Ensure currentPage valid when filteredProducts changes
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / rowsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredProducts, rowsPerPage, currentPage]);

  // Pagination slice
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, currentPage, rowsPerPage]);

  // --- Chart data ---
  const stockChartData = useMemo(() => {
    const data = products.map(p => ({
      name: p.name.length > 12 ? p.name.slice(0, 12) + '…' : p.name,
      fullName: p.name,
      quantity: p.quantity,
      threshold: p.min_threshold,
      isLowStock: p.quantity <= p.min_threshold
    }))
    // sort asc by quantity to show lowest stock first
    .sort((a, b) => a.quantity - b.quantity);

    // limit to selected count
    return data.slice(0, stockLimit);
  }, [products, stockLimit]);

  // Sales daily totals — use full date for tooltip label (dd/mm/yyyy)
  const weeklySalesData = useMemo(() => {
    // Build a map keyed by full date string (dd/mm/yyyy) to preserve exact day
    const map: Record<string, number> = {};
    sales.forEach(s => {
      const d = new Date(s.sale_date);
      const key = d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      map[key] = (map[key] || 0) + (s.total_amount ?? 0);
    });
    // Convert to array sorted by date ascending
    return Object.entries(map).map(([date, total]) => ({
      date,
      total
    })).sort((a, b) => {
      const da = new Date(a.date.split('/').reverse().join('-'));
      const db = new Date(b.date.split('/').reverse().join('-'));
      return +da - +db;
    });
  }, [sales]);

  // --- Custom Sales tooltip (same style as stock tooltip, only sale date + total) ---
  const SalesTooltip: React.FC<any> = ({
    active,
    payload
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.date}</p>
          <p className="text-sm text-foreground">Total : {formatPrice(data.total)}</p>
        </div>;
    }
    return null;
  };

  // --- Custom Stock tooltip (reused style) ---
  const StockTooltip: React.FC<any> = ({
    active,
    payload
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.fullName}</p>
          <p className="text-sm text-foreground">Quantité: {data.quantity}</p>
          <p className="text-sm text-foreground">Seuil: {data.threshold}</p>
          {data.isLowStock && <p className="text-sm text-foreground font-medium">Stock faible!</p>}
        </div>;
    }
    return null;
  };

  // --- Selection handlers ---
  const handleToggleProduct = (id: string) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const handleToggleAll = () => {
    const allIds = filteredVisibleProducts.map(p => p.id);
    if (selectedProducts.size === allIds.length) setSelectedProducts(new Set());else setSelectedProducts(new Set(allIds));
  };
  const handleDeleteSelected = async () => {
    if (selectedProducts.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedProducts.size} produit(s) ?`)) return;
    for (const id of Array.from(selectedProducts)) {
      await deleteProduct(id);
    }
    setSelectedProducts(new Set());
  };

  // --- Quantity change with 2s debounce, allow empty during typing ---
  const onQuantityChange = (product: Product, rawValue: string) => {
    // store raw string to allow empty input while typing
    setLocalQuantities(prev => ({
      ...prev,
      [product.id]: rawValue === '' ? '' : Number(rawValue)
    }));

    // clear previous timer
    if (debounceTimers.current[product.id]) {
      clearTimeout(debounceTimers.current[product.id]);
    }

    // set new timer
    debounceTimers.current[product.id] = setTimeout(async () => {
      const val = rawValue === '' ? NaN : Number(rawValue);
      // don't save if empty or invalid
      if (rawValue === '' || isNaN(val) || val < 0) {
        delete debounceTimers.current[product.id];
        return;
      }
      try {
        await updateProduct(product.id, {
          quantity: val
        });
        toast({
          title: 'Quantité mise à jour',
          description: `${product.name}: ${val} unités`
        });
      } catch {
        toast({
          title: 'Erreur',
          description: `Impossible de modifier la quantité pour ${product.name}`,
          variant: 'destructive'
        });
      }
      delete debounceTimers.current[product.id];
    }, 2000);
  };
  const handleAdjustQuantity = (product: Product, delta: number) => {
    const current = localQuantities[product.id] === '' ? product.quantity : Number(localQuantities[product.id] ?? product.quantity);
    const newVal = current + delta;
    // update immediately local and schedule save
    onQuantityChange(product, String(Math.max(0, newVal)));
  };

  // Helper to compute total pages
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / rowsPerPage));

  // When currentPage becomes invalid (e.g. filteredProducts decreased), clamp it
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // --- Barcode scanned handler resets low-stock filter (like earlier)
  const handleBarcodeScanned = (barcode: string) => {
    setSearchQuery(barcode);
    setShowLowStockOnly(false);
    setCurrentPage(1);
  };

  // Mettre à jour les produits depuis OpenFoodFacts
  const handleUpdateFromOpenFood = async () => {
    const productsWithBarcodes = products.filter(p => p.barcode && p.barcode.trim());
    if (productsWithBarcodes.length === 0) {
      toast({
        title: 'Aucun produit à mettre à jour',
        description: 'Aucun produit avec code-barres trouvé',
        variant: 'destructive'
      });
      return;
    }
    if (!window.confirm(`Mettre à jour ${productsWithBarcodes.length} produit(s) depuis OpenFoodFacts ?`)) {
      return;
    }
    setIsUpdatingFromOpenFood(true);
    let updated = 0;
    let failed = 0;
    for (const product of productsWithBarcodes) {
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke('barcode-lookup', {
          body: {
            barcode: product.barcode
          }
        });
        const offName = data?.product?.name as string | undefined;
        const offImage = data?.product?.image_url as string | undefined;
        const found = data?.success && data?.found && data?.product;
        if (!error && found) {
          const updates: Record<string, any> = {};
          const currentName = (product.name || '').trim();
          // Mettre à jour le nom seulement s'il est vide ou trop court
          if ((!currentName || currentName.length < 3) && offName) {
            updates.name = offName;
          }
          // Mettre à jour l'image si absente
          if (!product.image_url && offImage) {
            updates.image_url = offImage;
          }
          if (Object.keys(updates).length > 0) {
            await updateProduct(product.id, updates);
            updated++;
          } else {
            failed++;
          }
        } else {
          failed++;
        }
      } catch (error) {
        console.error(`Erreur pour ${product.barcode}:`, error);
        failed++;
      }
    }
    setIsUpdatingFromOpenFood(false);
    toast({
      title: 'Mise à jour terminée',
      description: `${updated} produit(s) mis à jour, ${failed} échec(s)`,
      variant: updated > 0 ? 'default' : 'destructive'
    });
  };

  // Auto-enrich products needing details from OpenFoodFacts once
  useEffect(() => {
    if (autoEnrichedRef.current || isUpdatingFromOpenFood) return;
    const needing = products.filter(p => p.barcode && p.barcode.trim() && ((p.name || '').trim().length < 3 || !p.image_url)).slice(0, 25);
    if (needing.length === 0) return;
    autoEnrichedRef.current = true;
    (async () => {
      setIsUpdatingFromOpenFood(true);
      let updatedCount = 0;
      for (const product of needing) {
        try {
          const {
            data,
            error
          } = await supabase.functions.invoke('barcode-lookup', {
            body: {
              barcode: product.barcode
            }
          });
          const offName = data?.product?.name as string | undefined;
          const offImage = data?.product?.image_url as string | undefined;
          const found = data?.success && data?.found && data?.product;
          if (!error && found) {
            const updates: Record<string, any> = {};
            const currentName = (product.name || '').trim();
            if ((!currentName || currentName.length < 3) && offName) updates.name = offName;
            if (!product.image_url && offImage) updates.image_url = offImage;
            if (Object.keys(updates).length > 0) {
              await updateProduct(product.id, updates);
              updatedCount++;
            }
          }
        } catch {}
      }
      setIsUpdatingFromOpenFood(false);
      if (updatedCount > 0) {
        toast({
          title: 'Produits enrichis',
          description: `${updatedCount} produit(s) complété(s) automatiquement depuis OpenFoodFacts`
        });
      }
    })();
  }, [products]);

  // Detect duplicates
  const detectDuplicates = useCallback(() => {
    const duplicateGroups: Array<{key: string; products: Product[]; type: 'barcode' | 'name'}> = [];
    
    // Group by barcode (excluding empty barcodes)
    const barcodeMap = new Map<string, Product[]>();
    products.forEach(p => {
      if (p.barcode && p.barcode.trim()) {
        const key = p.barcode.trim().toLowerCase();
        if (!barcodeMap.has(key)) {
          barcodeMap.set(key, []);
        }
        barcodeMap.get(key)!.push(p);
      }
    });
    
    // Add barcode duplicates
    barcodeMap.forEach((prods, key) => {
      if (prods.length > 1) {
        duplicateGroups.push({ key, products: prods, type: 'barcode' });
      }
    });
    
    // Group by exact name (case-insensitive)
    const nameMap = new Map<string, Product[]>();
    products.forEach(p => {
      const key = p.name.trim().toLowerCase();
      if (!nameMap.has(key)) {
        nameMap.set(key, []);
      }
      nameMap.get(key)!.push(p);
    });
    
    // Add name duplicates (only if not already in barcode duplicates)
    nameMap.forEach((prods, key) => {
      if (prods.length > 1) {
        // Check if these products are already in barcode duplicates
        const alreadyInBarcodes = prods.every(p => 
          duplicateGroups.some(g => g.products.some(gp => gp.id === p.id))
        );
        if (!alreadyInBarcodes) {
          duplicateGroups.push({ key, products: prods, type: 'name' });
        }
      }
    });
    
    return duplicateGroups;
  }, [products]);

  // Check for duplicates on products change
  useEffect(() => {
    const dups = detectDuplicates();
    setDuplicates(dups);
  }, [detectDuplicates]);

  // Use imperativeHandle to expose methods to parent component
  useImperativeHandle(ref, () => ({
    activateLowStockFilter: () => {
      setShowLowStockOnly(true);
    }
  }), []);

  const handleDeleteDuplicates = async (productIds: string[]) => {
    for (const id of productIds) {
      await deleteProduct(id);
    }
  };
  return <div ref={tableRef} className="space-y-6">
      <DuplicateProductsModal 
        open={showDuplicatesModal}
        onOpenChange={setShowDuplicatesModal}
        duplicates={duplicates}
        onDeleteProducts={handleDeleteDuplicates}
      />
      
      {/* Products table */}
      <Card className="group transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 border-border/50">
                  <CardHeader>
              <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/20 group-hover:from-green-500/20 group-hover:to-green-600/30 transition-transform duration-200 hover:scale-105">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1 text-green-700 dark:text-green-300">
                  Inventory
                </p>
                <p className="text-xl font-bold text-green-800 dark:text-green-200">
                  Produits
                </p>
              </div>
              </div>
          </CardHeader>
        <CardContent>

          {/* Actions: search, scanner, filter, delete */}
      <div className="sticky top-16 z-10 bg-background border-b border-border/50 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 py-2 px-1 -mx-1 mb-4">
        <div className="sr-only" aria-live="polite">
          Table des produits. {filteredProducts.length} produits au total. Page {currentPage} sur {totalPages}.
          {showLowStockOnly ? 'Filtre actif : stock bas uniquement.' : ''} 
          {selectedProducts.size > 0 ? `${selectedProducts.size} produit(s) sélectionné(s).` : ''}
        </div>
        <div className="w-full sm:flex-1 sm:min-w-0">
          {/* SearchBar (if your SearchBar accepts className prop, it will stretch) */}
          <SearchBar searchQuery={searchQuery} onSearchChange={(v: string) => {
              setSearchQuery(v);
              setCurrentPage(1); // reset pagination on search change
            }} searchFilter="name" onFilterChange={() => {}} onClearSearch={() => {
              setSearchQuery('');
              setCurrentPage(1);
            }} showNoResults={false} className="w-full" disabled={!hasAccess} placeholder={!hasAccess ? "Recherche désactivée en mode démo" : "Rechercher un produit..."} />
        </div>

        <div className="flex items-center justify-between sm:justify-start gap-2">
          <div onClick={() => {
              setShowLowStockOnly(prev => !prev);
              setCurrentPage(1); // reset page when toggling low-stock
            }} className={`flex items-center gap-2 text-sm cursor-pointer select-none border rounded-md px-[10px] py-[10px] transition-colors ${showLowStockOnly ? 'bg-destructive/10 border-destructive text-destructive font-medium' : 'text-muted-foreground border-border hover:bg-muted/50'}`}>
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <span>Stock bas</span>
          </div>

          <BarcodeScanner autoStart onBarcodeScanned={handleBarcodeScanned} trigger={<Button variant="outline" size="sm"><Camera className="w-4 h-4 mr-2" /><span>Scanner</span></Button>} />

          {duplicates.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowDuplicatesModal(true)}
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {duplicates.length} doublon{duplicates.length > 1 ? 's' : ''}
            </Button>
          )}

          {isAdmin}

          {isAdmin && selectedProducts.size > 0 && <Button variant="destructive" size="sm" onClick={handleDeleteSelected}>
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Supprimer ({selectedProducts.size})</span>
              <span className="sm:hidden">({selectedProducts.size})</span>
            </Button>}
        </div>
      </div>

          <div className="overflow-x-auto -mx-6 px-6 lg:mx-0 lg:px-0">
            <Table className="table-fixed min-w-[1000px]" aria-label="Tableau des produits en stock" role="table">
            <caption className="sr-only">
              Inventaire des produits. Ce tableau affiche {filteredProducts.length} produits avec leur image, nom, code-barres, 
              prix, quantité en stock, seuil minimum, valeur totale et actions disponibles. 
              Page {currentPage} sur {totalPages}. {showLowStockOnly ? 'Filtre actif : produits avec stock bas.' : ''}
            </caption>
            <TableHeader>
              <TableRow>
                 {isAdmin && <TableHead className="w-10 " scope="col">
                    <Checkbox checked={selectedProducts.size === filteredVisibleProducts.length && filteredVisibleProducts.length > 0} onCheckedChange={handleToggleAll} aria-label="Sélectionner tous les produits" />
                  </TableHead>}
                  <TableHead className="w-[10%] hidden sm:table-cell" scope="col">Image</TableHead>
                  <TableHead className="w-[15%] sm:w-[18%]" scope="col">Nom</TableHead>
                  <TableHead className="w-[15%] sm:w-[12%] hidden sm:table-cell" scope="col">Code-barres</TableHead>
                {isAdmin && <TableHead className="w-[12%] sm:w-[10%] hidden lg:table-cell" scope="col">Prix</TableHead>}
                <TableHead className="w-[20%] sm:w-[18%]" scope="col">Quantité</TableHead>
                {isAdmin && <TableHead className="w-[10%] hidden md:table-cell" scope="col">Seuil</TableHead>}
                {isAdmin && <TableHead className="w-[12%] sm:w-[10%] hidden lg:table-cell" scope="col">Valeur</TableHead>}
                {isAdmin && <TableHead className="w-[28%] sm:w-[16%]" scope="col">Actions</TableHead>}
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedProducts.map(product => {
                const raw = localQuantities[product.id];
                const currentQty = raw === '' ? '' : raw ?? product.quantity;
                const isLow = (typeof currentQty === 'number' ? currentQty : product.quantity) < product.min_threshold;
                const isSelected = selectedProducts.has(product.id);
                const isHidden = hiddenProducts.includes(product);
                return <TableRow key={product.id} data-state={isSelected ? 'selected' : undefined} onClick={() => handleToggleProduct(product.id)} className={`cursor-pointer ${isHidden ? 'blur-sm opacity-50 pointer-events-none' : ''}`}>
                    {isAdmin && <TableCell scope="row">
                        <div onClick={e => e.stopPropagation()}>
                          <Checkbox checked={isSelected} onCheckedChange={() => handleToggleProduct(product.id)} aria-label={`Sélectionner ${product.name}`} />
                        </div>
                      </TableCell>}

                    <TableCell className="hidden sm:table-cell">
                      {product.image_url ? <img src={product.image_url} alt={`Image du produit ${product.name}`} className="w-12 h-12 object-cover rounded-md border" /> : <div className="w-12 h-12 bg-muted rounded-md border flex items-center justify-center" aria-label="Aucune image disponible">
                          <Package className="w-6 h-6 text-muted-foreground" />
                        </div>}
                    </TableCell>

                    <TableCell className="font-medium">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{product.name}</span>
                          {product.cycle_de_vie && <StatusBadge status={product.cycle_de_vie} />}
                        </div>
                        <div className="sm:hidden text-xs text-muted-foreground font-mono">
                          {product.barcode || 'Pas de code-barres'}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell className="font-mono text-sm truncate hidden sm:table-cell">{product.barcode || '-'}</TableCell>

                    {isAdmin && <TableCell className="hidden lg:table-cell">{formatPrice(product.price)}</TableCell>}

                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" onClick={e => {
                        e.stopPropagation();
                        handleAdjustQuantity(product, -1);
                      }} className="border-2 py-0 px-[8px] rounded-sm">
                          <Minus className="w-4 h-4" />
                        </Button>

                        <input inputMode="numeric" pattern="[0-9]*" type="text" value={currentQty as string | number} onChange={e => onQuantityChange(product, e.target.value)} onClick={e => e.stopPropagation()} className={`w-16 text-center bg-transparent outline-none font-medium ${isLow ? 'text-destructive' : ''}`} />

                        <Button size="icon" variant="ghost" onClick={e => {
                        e.stopPropagation();
                        handleAdjustQuantity(product, 1);
                      }} className="border-2 px-[8px] rounded-sm">
                          <Plus className="w-4 h-4" />
                        </Button>

                        {isLow && <AlertTriangle className="w-4 h-4 text-destructive" />}
                      </div>
                    </TableCell>

                    {isAdmin && <TableCell className="hidden md:table-cell">{product.min_threshold}</TableCell>}
                    {isAdmin && <TableCell className="hidden lg:table-cell">{formatPrice(product.price * (typeof currentQty === 'number' ? currentQty : product.quantity))}</TableCell>}

                    {isAdmin && <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={e => {
                        e.stopPropagation();
                        onEditProduct(product);
                      }} aria-label="Modifier">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <div onClick={e => e.stopPropagation()}>
                            <QRCodeModal product={product} />
                          </div>
                          <Button variant="outline" size="sm" onClick={e => {
                        e.stopPropagation();
                        if (product.barcode) {
                          navigator.clipboard.writeText(product.barcode);
                          toast({
                            title: 'Copié',
                            description: `Code-barres ${product.barcode}`
                          });
                        } else {
                          toast({
                            title: 'Aucun code-barres',
                            description: "Ce produit n’a pas de code-barres",
                            variant: 'destructive'
                          });
                        }
                      }} aria-label="Copier le code-barres">
                            <Copy className="w-4 h-4 mr-2" />
                          </Button>
                          
                        </div>
                      </TableCell>}
                  </TableRow>;
              })}
            </TableBody>
            </Table>
          </div>

          {/* Message pour produits masqués */}
          {hiddenProducts.length > 0 && <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 mb-1">
                <strong>{hiddenProducts.length}</strong> produit{hiddenProducts.length > 1 ? 's' : ''} masqué{hiddenProducts.length > 1 ? 's' : ''} (mode démo)
              </p>
              <p className="text-xs text-amber-600">
                Passez à Premium pour accéder à tous vos produits
              </p>
            </div>}

          {/* pagination controls */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Lignes par page :</span>
              <select className="bg-background border rounded-md px-2 py-1 text-sm" value={rowsPerPage} onChange={e => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1 || !hasAccess}>Précédent</Button>
              <span>Page {currentPage} / {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || !hasAccess}>Suivant</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
});